/** Typen der Schnipsel-Oberfläche. Spiegeln `broker_hub_snippets` + die Zusatzspalten der Liste. */

/** Die Ebene, auf der eine Fassung liegt. `site_id = 0` ist global für alle sechs Portale. */
export const SNIPPET_GLOBAL_SITE_ID = 0;

export interface SnippetListRow {
  id: number;
  slug: string;
  site_id: number;
  broker_id: number | null;
  title: string | null;
  sha256: string;
  enabled: number;
  revision: number;
  updated_by: string | null;
  updated_at?: string;
  /** aus dem LEFT JOIN — null, wenn kein Broker-Bezug bzw. globale Ebene */
  broker_slug: string | null;
  broker_name: string | null;
  site_key: string | null;
  site_name: string | null;
  /** Portale, deren ausgespielter Stand dem aktuellen Fingerabdruck entspricht. */
  aktuell_auf: number;
  /** Portale mit hinterlegtem Fehlergrund. */
  fehler_auf: number;
  /**
   * Wie viele Portale diese Fassung ueberhaupt betreffen — vom Server, nicht aus `sites.length`.
   *
   * ⚠ Ein globaler Schnipsel geht NICHT an Portale, die eine eigene aktive Fassung desselben
   * Namens haben. Mit `sites.length` als Nenner kann der Zaehler ihn dann nie erreichen, und die
   * Uebersicht zeigt dauerhaft Warnfarbe fuer einen vollstaendig korrekten Zustand.
   */
  ziel_anzahl: number;
  /**
   * Gesetzt = aus der ARBEITSLISTE geraeumt (A40-B3). Nicht dasselbe wie `enabled = 0`.
   *
   * ⚠ Es gibt bewusst kein Loeschen: die Tabelle hat weder Fremdschluessel noch Cascades, und
   * Pruefen-dann-Loeschen waere ueber den geteilten Schluessel `snippet:<slug>` ein Rennen mit
   * echtem Portal-Inhalt. Die Zeile bleibt samt Historie.
   */
  archived_at?: string | null;
  archived_by?: string | null;
}

export interface SnippetSiteState {
  site_id: number;
  site_key: string;
  site_name?: string | null;
  deployed_sha256: string | null;
  deployed_at: string | null;
  last_error: string | null;
}

export interface SnippetVersion {
  version: number;
  change_note: string | null;
  created_by: string | null;
  created_at: string;
  snapshot_bytes: number;
}

/**
 * Wer besitzt einen Namen auf einem Portal? `level = null` heisst „auf diesem Portal gibt der
 * Name nichts aus" — ein eigener Zustand, nicht ein Fehler.
 */
export interface SnippetOwnership {
  site_id: number;
  site_key: string;
  owner_id: number | null;
  level: 'global' | 'portal' | null;
}

/** Was auf EINEM Portal geschah. Spiegelt `SnippetWirkung` im Dienst. */
export type SnippetWirkung =
  | 'gespeichert'
  | 'geraeumt'
  | 'unveraendert'
  | 'uebersprungen'
  | 'fehlgeschlagen';

/**
 * Ergebnis eines Ausspiel- oder Vorschau-Laufs, je Portal.
 *
 * ⚠ Seit A40-B2 liefert der Dienst GENAU EINE Zeile je angefragtem Portal — auch fuer „nichts zu
 * tun". Vorher fehlte die, und „N von M" bedeutete nicht „von den sechs", sondern „von denen, wo
 * etwas zu tun war". `wirkung` und `owner_id` sind bei der Vorschau nicht gesetzt, deshalb optional.
 */
export interface SnippetDeployResult {
  site_id: number;
  site_key: string;
  ok: boolean;
  skipped?: boolean;
  error?: string;
  html?: string;
  css?: string;
  /**
   * Herkunft des Zielportals, gegen die der Vorschau-iframe relative Adressen aufloest — oder
   * `null`. Vom Server geprueft (`vorschauHerkunft`), nur bei `render-preview` gesetzt.
   */
  baseUrl?: string | null;
  wirkung?: SnippetWirkung;
  owner_id?: number | null;
  owner_level?: 'global' | 'portal' | null;
  /**
   * Was die Vorschau ueber ihre EIGENE Typografie sagen kann — nur bei `render-preview` gesetzt.
   *
   * `fehlt`    = fuer dieses Portal wurde nie gemessen, die Vorschau zeigt Browser-Vorgaben.
   * `veraltet` = das Portal hat sein Theme seit der Messung geaendert.
   */
  vorschauTypografie?: 'ok' | 'fehlt' | 'veraltet';
}
